import { useCallback } from 'react'

import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/datatable.graphql'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  TaskActionsHook,
  TaskCustomComponent,
  TaskDefinition,
} from 'modules/inbox_tasks/lib/abstraction'
import { useUpdateTaskStatuses } from 'modules/inbox_tasks/lib/updateTaskStatus/updateTaskStatus'

import BasicTaskContent from './BasicTask'
import { getBasicTaskMetadata } from './BasicTask/metadata'
import { useCompleteStepActionTask } from './completeTask/completeTask'
import DataInputContent from './DataInput'
import { getDataInputMetadata } from './DataInput/metadata'
import { hasDataInputSubmitHandler, submitDataInputTask } from './DataInput/submitRegistry'
import { ActionType, StepActionContentComponent, parseConfigurationJson } from './helpers'
import TrainingContent from './Training'
import { getTrainingMetadata } from './Training/metadata'
import UploadDocumentContent from './UploadDocument'
import { getUploadDocumentMetadata } from './UploadDocument/metadata'

const ACTION_COMPONENTS: Record<ActionType, StepActionContentComponent> = {
  basic_task: BasicTaskContent,
  training: TrainingContent,
  upload_document: UploadDocumentContent,
  data_input: DataInputContent,
}

const isActionType = (value: string): value is ActionType => value in ACTION_COMPONENTS

const EmployeeLifecycleStepActionContent: TaskCustomComponent<'EmployeeLifecycleStepAction'> = ({
  taskId,
  taskable,
  affectee,
}) => {
  const rawActionType = taskable?.actionType ?? 'basic_task'
  const Component = isActionType(rawActionType)
    ? ACTION_COMPONENTS[rawActionType]
    : ACTION_COMPONENTS.basic_task

  return (
    <Component
      taskId={taskId}
      stepActionId={taskable?.id}
      files={taskable?.uploadedDocuments ?? []}
      affectee={affectee}
      configurationJson={taskable?.configurationJson}
    />
  )
}

const useEmployeeLifecycleStepActions: TaskActionsHook<'EmployeeLifecycleStepAction'> = ({
  taskId,
  taskable,
}) => {
  const updateTaskStatuses = useUpdateTaskStatuses()
  const completeStepActionTask = useCompleteStepActionTask()

  // Shared completion path used by both the dropdown's "mark-as-done" entry
  // (for `manual` kinds with a status dropdown) and the primary "Complete"
  // button (for non-manual kinds like `fill` / `complete`, which the inbox
  // Footer only renders when `onClickPrimaryAction` is provided — see
  // `modules/inbox_tasks/activities/Task/Footer/index.tsx:71`).
  const completeTask = useCallback(async (): Promise<boolean | void> => {
    if (!taskable?.id) return false

    // `data_input` writes the affectee's employee + contract data on
    // completion. Submit the form first (with validation); if the submit
    // fails — client-side validation or a backend error — block the
    // transition so the task does not flip to done with unsaved data.
    //
    // The submit handler is registered by the DataInput renderer when the
    // panel mounts (see DataInput/index.tsx). Marking the task as done from
    // a context where the panel was never mounted (e.g. the list-view
    // dropdown) would otherwise skip submission entirely and advance the
    // workflow with no data persisted — treat that as a hard block.
    if (taskable.actionType === 'data_input') {
      if (!hasDataInputSubmitHandler(taskId)) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('employee_lifecycle.workflows.data_input.open_to_complete'),
        })
        return false
      }
      const submitOk = await submitDataInputTask(taskId)
      if (!submitOk) return false
    }

    await completeStepActionTask({
      stepActionId: taskable.id,
      taskId,
    })
    return true
  }, [completeStepActionTask, taskId, taskable?.id, taskable?.actionType])

  const handleDropdownAction = useCallback(
    async ({ actionId }: { actionId: string }) => {
      const statuses: Record<string, TasksValueObjectsTaskStatusEnum> = {
        'mark-as-done': 'done',
        'mark-as-todo': 'todo',
        'mark-as-in-progress': 'in_progress',
        'mark-as-discarded': 'discarded',
      }

      const newStatus = statuses[actionId]

      if (!newStatus) return

      if (newStatus === 'done') {
        return completeTask()
      }

      await updateTaskStatuses({
        taskIds: [taskId],
        status: newStatus,
      })

      return true
    },
    [completeTask, taskId, updateTaskStatuses]
  )

  return {
    onClickPrimaryAction: completeTask,
    onClickDropdownAction: handleDropdownAction,
  }
}

export const EmployeeLifecycleStepActionTaskDefinition: TaskDefinition<'EmployeeLifecycleStepAction'> =
  {
    taskableTypename: 'EmployeeLifecycleStepAction',
    initialState: {},
    component: EmployeeLifecycleStepActionContent,
    useActions: useEmployeeLifecycleStepActions,
    getUrl: ({ info, navigation }) => {
      if (info.actionType !== 'training') return null

      const config = parseConfigurationJson(info.configurationJson)
      const trainingId = config?.training_id

      if (!trainingId) return null

      return navigation.trainings.list.details_revamp.view.participants.fullPath({
        id: String(trainingId),
      })
    },
    getMetadata: ({ info }) => {
      switch (info.actionType) {
        case 'training':
          return getTrainingMetadata()
        case 'upload_document':
          return getUploadDocumentMetadata(info.configurationJson)
        case 'data_input':
          return getDataInputMetadata(info.configurationJson)
        case 'basic_task':
        default:
          return getBasicTaskMetadata(info.configurationJson)
      }
    },
  }
